import React, { FormEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';
import { Table } from '../../common';
import { Link } from 'react-router-dom';
import { getInvoiceStatus, getInvoiceReoccur, formatMoney } from '../../../utils';
import { IInvoice, InvoiceStatus } from '../../../@types';
import dayjs from 'dayjs';
import { AddSquareIcon, DownloadIcon, FilterIcon } from '../../../assets/icons';
import { IPaginationMeta } from '../../../@types';
import InvoiceFilter from '../InvoiceFilter';
import { DropDown } from '../../common';
import HeadingIcon from '../../../assets/icons/HeadingIcon';
import { Statuses } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import { Filter } from '../../../@types/invoice';
import { getStatusClass, getStatusColor } from '../Status/status';
import DropdownButton from '../../common/Table/MobileTableComponent/DropdownOptions';

interface InvoicesTableProps {
  invoices: IInvoice[];
  isLoading: boolean;
  fetchMoreData: () => void;
  meta: IPaginationMeta;
  onSearchData?: (text: string) => void;
  handleSort?: (text: string) => void;
  handleSearch?: (text: string) => void;
  showHead?: boolean;
  handleFilter?: (filter: Filter) => void;
  newLink?: string;
  title?: string;
  error?: boolean;
  isSearching?: boolean;
  headings?: {
    [key: string]: string;
  };
  data?: any[];
  searchResult: any[];
  selectedFilter?: Filter | undefined;
}

const InvoicesTable = ({
  selectedFilter,
  invoices,
  isLoading,
  fetchMoreData,
  meta,
  handleSort,
  handleSearch,
  searchResult,
  onSearchData,
  showHead = true,
  handleFilter,
}: InvoicesTableProps) => {
  const [showFilter, setShowFilter] = React.useState(false);
  const [isReoccurring, setIsReoccurring] = React.useState<boolean | null>(null);

  const [status, setStatus] = React.useState<InvoiceStatus | ''>('');
  const [dropdown, setDropdown] = React.useState(false);
  const handleFilterIconClick = () => {
    setShowFilter(true);
  };

  const handleCloseModal = () => {
    setShowFilter(false);
  };

  const getDaySuffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return day + 'th';
    }

    switch (day % 10) {
      case 1:
        return day + 'st';
      case 2:
        return day + 'nd';
      case 3:
        return day + 'rd';
      default:
        return day + 'th';
    }
  };
  const navigate = useNavigate();
  const handleSearchSelect = (item: any) => {
    item.status === Statuses.DRAFT.toLowerCase()
      ? navigate(`/invoices/${item.id}`)
      : navigate(`/invoices/${item.id}/view`);
  };
  const observer = useRef<IntersectionObserver | null>(null);
  const lastInvoiceRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && meta.hasMoreData) {
          fetchMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, meta],
  );

  const memoizedSelectedFilter = useMemo(
    () => selectedFilter,
    [
      selectedFilter && 'status' in selectedFilter ? selectedFilter.status : null,
      selectedFilter && 'isReoccurring' in selectedFilter ? selectedFilter.isReoccurring : null,
    ],
  );

  const renderInvoicesData = (invoices: IInvoice[]) => {
    return invoices
      .filter((invoice: IInvoice) => {
        if (isReoccurring !== null && invoice.isReoccurring !== isReoccurring) {
          return false;
        }
        return true;
      })
      .map((invoice: IInvoice, index) => {
        const statusCapitalized = invoice.status.charAt(0).toUpperCase() + invoice.status?.slice(1);
        const isDraft = statusCapitalized === Statuses.DRAFT;
        const linkTo = isDraft ? `/invoices/${invoice.id}` : `/invoices/${invoice.id}/view`;
        const linkText = isDraft ? invoice.invoiceNumber : invoice.invoiceNumber;
        const statusClass = getStatusClass(statusCapitalized);
        const statusColor = getStatusColor(statusCapitalized);
        const invoiceId = invoice.id;

        return {
          invoiceNumbers: (
            <div ref={index === invoices.length - 1 ? lastInvoiceRef : null}>
              <Link className="text-violet-800" to={linkTo}>
                {linkText}
              </Link>
            </div>
          ),
          customer: (
            <Link
              className="xl:text-violet-800"
              to={`/customers/${invoice?.customer?.id}/view`}
            >{`${invoice?.customer?.firstName} ${invoice?.customer?.lastName}`}</Link>
          ),
          issueDate: `${dayjs(invoice.issueDate).format('MMM')} ${getDaySuffix(
            dayjs(invoice.issueDate).date(),
          )} ${dayjs(invoice.issueDate).format('YYYY')}`,
          invoiceNumber: invoice.invoiceNumber,
          status: (
            <div className={`${statusClass}  `} style={{ backgroundColor: statusColor }}>
              <Link className="block" to={linkTo}>
                {getInvoiceStatus(statusCapitalized)}
              </Link>
            </div>
          ),
          isReoccurring: getInvoiceReoccur(invoice.isReoccurring),
          inventories: formatMoney(invoice.balanceDue, invoice.currency),
          invoiceId: invoiceId,
        };
      });
  };

  const renderInvoicesTableHead = useMemo(
    () => ({
      invoiceNumbers: { title: 'Invoice ID' },
      customer: { title: 'Customer', icon: <HeadingIcon /> },
      issueDate: { title: 'Issue Date', icon: <HeadingIcon /> },
      isReoccurring: { title: 'Type' },
      status: { title: 'Status' },
      inventories: { title: 'Balance Due', icon: <HeadingIcon /> },
    }),
    [],
  );
  const handleStatusChange = (newStatus: InvoiceStatus) => {
    console.log('newStatus', newStatus.toLowerCase());
    setStatus(newStatus.toLowerCase() as InvoiceStatus);
  };

  const handleIsReoccurringChange = (newIsReoccurring: boolean) => {
    setIsReoccurring(newIsReoccurring);
  };

  const onInvoiceFilterSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (handleFilter) {
      handleFilter({ status, isReoccurring });
    }
    setShowFilter(false);
  };

  const fetchPageData = (direction: 'up' | 'down') => {
    if (direction === 'down' && meta.hasMoreData) {
      console.log('down');
      fetchMoreData();
    }
  };

  const onSearch = (text: string) => {
    if (onSearchData && handleSearch) {
      handleSearch(text);
    }
  };

  console.log(isLoading);

  return (
    <>
      <div className="hidden md:block whitespace-nowrap">
        <Table
          // searchPlaceHolder="Search for innvoice by invoice number"
          searchPlaceHolder="Search"
          selectedFilter={selectedFilter}
          handleFilter={handleFilter}
          searchDisplayKey="invoiceNumber"
          searchResult={searchResult}
          head={renderInvoicesTableHead}
          data={renderInvoicesData(invoices)}
          isLoading={isLoading}
          showHead={showHead || undefined}
          handleSearchSelect={handleSearchSelect}
          buttons={[
            {
              text: 'New Invoice',
              href: '/invoices/create',
              icon: <AddSquareIcon />,
            },
            // {
            //   text: 'Import',
            //   icon: <DownloadIcon />,
            // },
            {
              text: '',
              icon: (
                <DropDown
                  onChange={handleFilterIconClick}
                  show={showFilter}
                  component={
                    <div className="flex ">
                      {' '}
                      <span>Filter</span> <FilterIcon color="#044E97" />{' '}
                    </div>
                  }
                >
                  <InvoiceFilter
                    isReoccurring={
                      memoizedSelectedFilter && 'isReoccurring' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.isReoccurring
                        : undefined
                    }
                    setStatus={handleStatusChange}
                    setIsReoccurring={handleIsReoccurringChange}
                    status={
                      memoizedSelectedFilter && 'status' in memoizedSelectedFilter
                        ? memoizedSelectedFilter.status
                        : undefined
                    }
                    onSubmit={onInvoiceFilterSubmit}
                    onClose={handleCloseModal}
                    showFilter
                  />
                </DropDown>
              ),
            },
          ]}
          showPagination
          handleSearch={onSearch}
          handleSort={handleSort}
          fetchMoreData={fetchPageData}
          meta={meta}
        />
        <DropdownButton />
      </div>
      <div className="block md:hidden mt-[12px] ">
        <div className="relative">
          <button
            className="bg-[#044E97] text-[#FFFFFF] text-[16px] absolute mt-[240px] z-30 ml-[18em] flex w-11 h-11 rounded-full justify-center items-center py-[12.5px] leading-4 first-letter font-semibold"
            onClick={() => setDropdown((prev) => !prev)}
          >
            {dropdown ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-6 h-6 inline-block items-center "
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-6 h-6 inline-block"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
          </button>
          {dropdown && (
            <div className="shadow-[0px 8px 16px 0px rgba(0,0,0,0.2)] w-[240px] bg-[#ffff] shadow-[0px 8px 16px 0px rgba(0,0,0,0.2)] z-[50] absolute top-[100%] left-0 ml-[2em] mt-[11em] h-fit">
              <h3 className="">
                <Link to={'/invoices/create'} className="text-[#000] px-[16px] hover:text-blue-500">
                  New Invoice
                </Link>
              </h3>
              <h3 className="text-[#000] py-[12px] px-[16px] ">
                <Link to={''} className="hover:text-blue-500">
                  Import
                </Link>
              </h3>
            </div>
          )}
        </div>
        <div className="flex md:hidden justify-center items-center">
          <Table
            // searchPlaceHolder="Search for innvoice by invoice number"
            searchPlaceHolder="Search "
            selectedFilter={selectedFilter}
            handleFilter={handleFilter}
            searchDisplayKey="invoiceNumber"
            searchResult={searchResult}
            head={renderInvoicesTableHead}
            data={renderInvoicesData(invoices)}
            isLoading={isLoading}
            showHead={showHead || undefined}
            handleSearchSelect={handleSearchSelect}
            hideFloatingScrollButton={true}
            buttons={[
              {
                text: '',
                icon: (
                  <div className="ml-[-25em]">
                    <DropDown
                      onChange={handleFilterIconClick}
                      show={showFilter}
                      component={
                        <div className="flex items-center h-[38px] ml-[26em] ">
                          <span className="text-[14px]">Filter</span>
                          <FilterIcon color="#044E97" />
                        </div>
                      }
                    >
                      <div className="">
                        <InvoiceFilter
                          isReoccurring={
                            memoizedSelectedFilter && 'isReoccurring' in memoizedSelectedFilter
                              ? memoizedSelectedFilter.isReoccurring
                              : undefined
                          }
                          setStatus={handleStatusChange}
                          setIsReoccurring={handleIsReoccurringChange}
                          status={
                            memoizedSelectedFilter && 'status' in memoizedSelectedFilter
                              ? memoizedSelectedFilter.status
                              : undefined
                          }
                          onSubmit={onInvoiceFilterSubmit}
                          // showFilter={showFilter}
                          onClose={handleCloseModal}
                          showFilter
                        />
                      </div>
                    </DropDown>
                  </div>
                ),
              },
            ]}
            showPagination
            handleSearch={onSearch}
            handleSort={handleSort}
            fetchMoreData={fetchPageData}
            meta={meta}
            invoiceCard={true}
          />
        </div>
      </div>
    </>
  );
};

export default InvoicesTable;
